import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AUTH0_CONFIG } from '../config';
import { getProfileByUserId, getProfileByEmail, getPreferences } from '../services/api';
import { initAuthBridge } from '../services/authBridge';
import type { AuthUser, AuthContextValue } from '../types';

const AuthContext = createContext<AuthContextValue>({
  user: null,
  token: null,
  isLoading: false,
  emailNotVerified: false,
  hasProfile: null,
  needsRelogin: false,
  setHasProfile: () => {},
  login: async () => {},
  loginWithApple: async () => {},
  logout: async () => {},
  relogin: async () => {},
  checkEmailVerification: async () => {},
});

export const useAuthContext = (): AuthContextValue => useContext(AuthContext);

interface AuthInnerProps {
  children: ReactNode;
}

// Auth0's pre-login Action fires api.access.deny(...) for unverified emails,
// which surfaces here as an OAuthError with error='access_denied' — same
// error shape auth0-spa-js uses for both the redirect callback and silent
// token renewal failures.
const isEmailNotVerifiedError = (e: any): boolean => {
  const code: string = e?.error ?? '';
  const desc: string = (e?.error_description ?? e?.message ?? '').toLowerCase();
  return code === 'access_denied' || desc.includes('verify');
};

const AuthInner: React.FC<AuthInnerProps> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();
  const [token, setToken] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState<boolean>(false);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [needsRelogin, setNeedsRelogin] = useState<boolean>(false);

  const user: AuthUser | null = isAuthenticated && auth0User
    ? {
        sub: auth0User.sub ?? '',
        name: auth0User.name,
        email: auth0User.email,
        picture: auth0User.picture,
        emailVerified: auth0User.email_verified,
      }
    : null;

  // Wire up the API-layer bridge so the 401 interceptor can refresh tokens
  // and force logout without needing access to React context.
  useEffect(() => {
    const getCredentialsForBridge = async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH0_CONFIG.audience },
        });
        return { accessToken };
      } catch {
        return undefined;
      }
    };
    const handleAuthFailure = async () => {
      await AsyncStorage.removeItem('auth_token');
      setToken(null);
      setHasProfile(null);
      try { await auth0Logout({ logoutParams: { returnTo: window.location.origin } }); } catch {}
    };
    initAuthBridge(getCredentialsForBridge as any, handleAuthFailure);
  }, [getAccessTokenSilently, auth0Logout]);

  useEffect(() => {
    if (!error) return;
    console.error('[Auth] Auth0 error:', error.message, error);
    if (isEmailNotVerifiedError(error)) {
      setEmailNotVerified(true);
    }
  }, [error]);

  useEffect(() => {
    console.info('[Auth] user changed:', user ? `sub=${user.sub} email=${user.email} emailVerified=${user.emailVerified}` : 'null');
    if (user) {
      if (user.emailVerified === false) {
        console.info('[Auth] email not verified — blocking login');
        AsyncStorage.removeItem('auth_token');
        setToken(null);
        setEmailNotVerified(true);
        return;
      }

      setEmailNotVerified(false);
      getAccessTokenSilently({ authorizationParams: { audience: AUTH0_CONFIG.audience } })
        .then((accessToken: string) => {
          console.info('[Auth] getAccessTokenSilently result — token present:', !!accessToken);
          if (accessToken) {
            AsyncStorage.setItem('auth_token', accessToken);
            setToken(accessToken);
            // Check if user has a profile — drives Onboarding vs Main navigation
            if (user?.sub) {
              console.info('[Auth] checking profile for userId:', user.sub);
              getProfileByUserId(encodeURIComponent(user.sub))
                .then(res => {
                  console.info('[Auth] profile found by userId:', res.data?.id);
                  const profile = res.data;
                  setHasProfile(true);
                  // Persist search defaults for fast AsyncStorage reads
                  const pairs: [string, string][] = [];
                  if (profile?.gender === 'MALE' || profile?.gender === 'FEMALE') {
                    pairs.push(['pref_looking_for', profile.gender === 'MALE' ? 'FEMALE' : 'MALE']);
                  }
                  if (profile?.religion) pairs.push(['pref_search_religion', profile.religion]);
                  if (profile?.caste) pairs.push(['pref_search_caste', profile.caste]);
                  if (pairs.length > 0) AsyncStorage.multiSet(pairs);
                  // Override pref_search_religion with saved preference if set
                  // (runs in background — does not block navigation)
                  getPreferences().then(prefRes => {
                    const preferred = prefRes.data?.preferredReligions?.[0];
                    if (preferred) AsyncStorage.setItem('pref_search_religion', preferred);
                    else if (!profile?.religion) AsyncStorage.removeItem('pref_search_religion');
                  }).catch(() => {});
                })
                .catch(async (err: any) => {
                  console.info('[Auth] getProfileByUserId failed — status:', err?.status, 'message:', err?.message);
                  if (err?.status !== 404) {
                    console.info('[Auth] non-404 error — assuming profile exists');
                    setHasProfile(true);
                    return;
                  }
                  // 404 by userId — could be a stale token after account linking
                  if (user?.email) {
                    try {
                      const emailRes = await getProfileByEmail(user.email);
                      console.info('[Auth] profile found by email — needsRelogin, profileId:', emailRes.data?.id);
                      setNeedsRelogin(true);
                      return;
                    } catch (emailErr: any) {
                      console.info('[Auth] getProfileByEmail failed — status:', emailErr?.status);
                      if (emailErr?.status !== 404) {
                        console.info('[Auth] non-404 email error — assuming profile exists');
                        setHasProfile(true);
                        return;
                      }
                    }
                  }
                  setHasProfile(false);
                });
            }
          }
        })
        .catch(async (e: any) => {
          console.error('[Auth] getAccessTokenSilently error — clearing session:', e);
          await AsyncStorage.removeItem('auth_token');
          setToken(null);
          setHasProfile(null);
        });
    } else {
      console.info('[Auth] user is null — clearing session state');
      AsyncStorage.removeItem('auth_token');
      setToken(null);
      setHasProfile(null);
    }
  }, [user?.sub, user?.email, user?.emailVerified]);

  const login = async (): Promise<void> => {
    await loginWithRedirect({
      authorizationParams: { audience: AUTH0_CONFIG.audience, scope: 'openid profile email offline_access' },
    });
  };

  const loginWithApple = async (): Promise<void> => {
    await loginWithRedirect({
      authorizationParams: {
        audience: AUTH0_CONFIG.audience,
        scope: 'openid profile email offline_access',
        connection: 'apple',
      },
    });
  };

  const checkEmailVerification = async (): Promise<void> => {
    await loginWithRedirect({
      authorizationParams: { audience: AUTH0_CONFIG.audience, scope: 'openid profile email offline_access' },
    });
    // On success: user state changes → effect fires → emailVerified=true → normal login flow clears the flag
  };

  const logout = async (): Promise<void> => {
    setEmailNotVerified(false);
    setNeedsRelogin(false);
    await auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const relogin = async (): Promise<void> => {
    setEmailNotVerified(false);
    setNeedsRelogin(false);
    await auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <AuthContext.Provider value={{
      user, token, isLoading, emailNotVerified,
      hasProfile, needsRelogin, setHasProfile,
      login, loginWithApple, logout, relogin, checkEmailVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => (
  <Auth0Provider
    domain={AUTH0_CONFIG.domain}
    clientId={AUTH0_CONFIG.clientId}
    authorizationParams={{ redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined }}
    cacheLocation="localstorage"
    useRefreshTokens>
    <AuthInner>{children}</AuthInner>
  </Auth0Provider>
);
